/**
 * Polymarket CLOB client factory
 * Requires ethers Signer (from walletClientToEthersSigner)
 */

import { ClobClient, OrderType, Side } from '@polymarket/clob-client'
import type { Signer } from 'ethers'

const HOST = 'https://clob.polymarket.com'
const CHAIN_ID = 137

export interface CreateClobClientOptions {
  /** POLY_PROXY = 1 for Magic users, EOA = 0 for regular wallets */
  signatureType?: 0 | 1 | 2
}

export async function createClobClient(
  signer: Signer,
  funder: string,
  options?: CreateClobClientOptions,
): Promise<ClobClient> {
  const signatureType = options?.signatureType ?? 0
  const signerAddr = await signer.getAddress()
  console.debug(`[ClobClient] init: signerAddr=${signerAddr}, funder=${funder}, sigType=${signatureType}`)

  const tempClient = new ClobClient(HOST, CHAIN_ID, signer as any, undefined, signatureType, funder)

  // createApiKey may either throw (when throwOnError=true) or return { key: undefined }
  // (when throwOnError=false, which is default for tempClient). Handle both cases.
  let creds: Awaited<ReturnType<typeof tempClient.createApiKey>> | undefined
  try {
    creds = await tempClient.createApiKey()
  } catch {
    console.debug('[ClobClient] createApiKey threw, will try deriveApiKey')
  }

  if (!creds?.key) {
    console.debug('[ClobClient] createApiKey returned no key, trying deriveApiKey…')
    try {
      creds = await tempClient.deriveApiKey()
    } catch {
      console.debug('[ClobClient] deriveApiKey also threw')
    }
  }

  if (!creds?.key) {
    // Last resort: deriveApiKey may also return { key: undefined } without throwing
    throw new Error(`[ClobClient] failed to obtain API key for ${signerAddr}`)
  }

  console.debug('[ClobClient] API key obtained, key=', creds.key.slice(0, 8) + '…')

  return new ClobClient(HOST, CHAIN_ID, signer as any, creds, signatureType, funder, undefined, undefined, undefined, undefined, undefined, undefined, true)
}

export interface PlaceOrderParams {
  tokenId: string
  side: 'YES' | 'NO'
  amountUsd: number
  tickSize: string
  negRisk: boolean
}

/** Round price to the market's tickSize, preserving user intent.
 *  BUY — floor (user не платит больше, чем объявил).
 *  SELL — ceil (user не отдаёт дешевле, чем объявил).
 *  Оба направления могут снизить вероятность исполнения, но НЕ ухудшают
 *  экономику ордера с точки зрения юзера. 6-digit guard против FP-drift. */
function roundPriceToTick(price: number, tickSize: string, side: 'BUY' | 'SELL'): number {
  const tick = parseFloat(tickSize || '0.001')
  if (!isFinite(tick) || tick <= 0) return price
  const rounder = side === 'BUY' ? Math.floor : Math.ceil
  return Math.round(rounder(price / tick) * tick * 1e6) / 1e6
}

export async function placeOrder(client: ClobClient, params: PlaceOrderParams & { yesPrice?: number; noPrice?: number; useExactPrice?: boolean }): Promise<unknown> {
  const side = Side.BUY
  const rawPrice = params.side === 'YES' ? (params.yesPrice ?? 0.5) : (params.noPrice ?? 0.5)
  const adjusted = params.useExactPrice ? rawPrice : rawPrice + 0.02
  const ticked = roundPriceToTick(adjusted, params.tickSize, 'BUY')
  const price = Math.min(0.99, Math.max(0.01, ticked))
  const size = Math.max(5, params.amountUsd)
  const resp = await client.createAndPostOrder(
    {
      tokenID: params.tokenId,
      price,
      side,
      size,
    },
    { tickSize: params.tickSize as import('@polymarket/clob-client').TickSize, negRisk: params.negRisk },
    OrderType.GTC
  )
  return resp
}

export interface PlaceSellOrderParams {
  tokenId: string
  /** Number of outcome tokens (shares) to sell */
  size: number
  price: number
  tickSize: string
  negRisk: boolean
}

/** Place a limit SELL order (size in shares). */
export async function placeSellOrder(client: ClobClient, params: PlaceSellOrderParams): Promise<unknown> {
  const ticked = roundPriceToTick(params.price, params.tickSize, 'SELL')
  const price = Math.min(0.99, Math.max(0.01, ticked))
  const size = Math.max(1, params.size)
  const resp = await client.createAndPostOrder(
    {
      tokenID: params.tokenId,
      price,
      side: Side.SELL,
      size,
    },
    { tickSize: params.tickSize as import('@polymarket/clob-client').TickSize, negRisk: params.negRisk },
    OrderType.GTC
  )
  return resp
}
